/**
 * One macro against its target.
 *
 * Colour lives in the bar and nowhere else — the spec keeps labels neutral
 * grey, so the name and the target are `--ink-62` even though the fill beside
 * them is saturated. That restraint is what makes the three bars
 * readable as a set rather than as three competing things.
 *
 * The fill clamps at 100% but the number doesn't: going over target is worth
 * knowing, and the recolour says it without the bar overflowing its track.
 */

type MacroKind = "protein" | "carbs" | "fat";

interface MacroBarProps {
  kind: MacroKind;
  label: string;
  grams: number;
  targetGrams: number | null;
  /**
   * Three bars across a 402px phone instead of three down it.
   *
   * The only thing that changes is the unit. "Protein 96 g / Carbs 158 g / Fat
   * 45 g" does not fit one line at a legible size, and the three are read as a
   * set — the heading says which macro, and every macro in this app is in
   * grams. It stays in the label the assistive tree reads, where there is no
   * width to run out of.
   */
  compact?: boolean;
}

/**
 * The grams eaten. Not "eaten out of target".
 *
 * The denominator is gone from this row entirely. It read "142/230 g", which is
 * the bar underneath it said twice — the bar already shows the fraction, and
 * far faster than two numbers and a slash do. What it cost was the one figure
 * people actually want at a glance, which had to share the line with three
 * other glyphs and shrink to fit.
 *
 * It was worst when there was no target at all. The budget form lets you set
 * calories and leave the macro fields blank, on purpose — someone can know the
 * calorie budget they want and genuinely have no protein figure. Blank stores
 * zero, because the column is NOT NULL, so the row read "142/0 g" with the 142
 * in alert red, any intake at all being "over" a target of nothing.
 *
 * A target of zero is still treated as absent, because it decides the bar and
 * the colour: no target, no fill and no red, and the number is a plain fact
 * rather than a verdict.
 */
export function MacroBar({ kind, label, grams, targetGrams, compact = false }: MacroBarProps) {
  const target = targetGrams !== null && targetGrams > 0 ? targetGrams : null;
  const over = target !== null && grams > target;
  const pct = target !== null ? Math.min((grams / target) * 100, 100) : 0;

  return (
    <div className="macro">
      <div className="macro__head">
        <span className="macro__name">{label}</span>
        <span className="macro__value num" style={{ color: over ? "var(--alert-text)" : undefined }}>
          {Math.round(grams)}
          {!compact && <span className="macro__unit"> g</span>}
        </span>
      </div>
      <div
        className="macro__track"
        role="progressbar"
        aria-valuenow={Math.round(grams)}
        aria-valuemin={0}
        aria-valuemax={target ?? undefined}
        aria-label={`${label} in grams`}
      >
        <div
          className="macro__fill"
          style={{ width: `${pct}%`, background: over ? "var(--alert)" : `var(--${kind})` }}
        />
      </div>
    </div>
  );
}
